import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';
import axiosInstance from '../config/axios';
import { setToken, setUserData } from '../utils/sessionManager';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(location.state?.message || '');

  // Efecto para manejar los mensajes
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      const timer = setTimeout(() => {
        setMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    console.log('🚀 [Login] Iniciando login con:', formData.email);

    try {
      console.log('📡 [Login] Enviando petición al servidor...');
      const response = await axiosInstance.post('/auth/login', formData);
      console.log('✅ [Login Frontend] Respuesta del servidor:', response.data);
      
      const { token, usuario, requirePasswordChange } = response.data;
      
      // Guardar token y datos del usuario
      console.log('💾 [Login Frontend] Guardando token y datos del usuario...');
      setToken(token);
      setUserData(usuario);
      
      // Actualizar el contexto de autenticación
      console.log('🔄 [Login Frontend] Actualizando contexto de autenticación');
      await login(usuario);
      
      // Determinar la ruta de redirección
      let targetRoute = location.state?.from?.pathname || '/dashboard';
      if (requirePasswordChange) {
        console.log('🔐 [Login Frontend] Usuario necesita cambiar contraseña');
        targetRoute = '/perfil';
        localStorage.setItem('requirePasswordChange', 'true');
      }
      
      console.log('🔄 [Login Frontend] Redirigiendo a:', targetRoute);
      navigate(targetRoute, { replace: true });
      
    } catch (error) {
      console.error('❌ [Login] Error:', error);
      
      let errorMessage = 'Error al iniciar sesión';
      
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = error.response.data?.error === 'Usuario no encontrado'
            ? 'El correo electrónico no está registrado'
            : 'Contraseña incorrecta';
        } else {
          errorMessage = error.response.data?.error || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'No se pudo conectar con el servidor';
      }
      
      setError(errorMessage);
      
      // Mostrar el error por 5 segundos
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Iniciar Sesión
          </Typography>

          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo Electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Iniciar Sesión'}
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link to="/recuperar-password" style={{ textDecoration: 'none' }}>
                <Typography color="primary" variant="body2">
                  ¿Olvidaste tu contraseña?
                </Typography>
              </Link>
              <Link to="/registro" style={{ textDecoration: 'none' }}>
                <Typography color="primary" variant="body2" sx={{ mt: 1 }}>
                  ¿No tienes una cuenta? Regístrate
                </Typography>
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 